import { useLingui } from '@lingui/react/macro';
import { Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { InlineButton } from '@/components/button/inline_button';
import { DecimalInputField } from '@/components/input_field/decimal_input_field';
import { useOrderFormCoordinator } from '../hooks/useOrderFormCoordinator';
import { useOrderFormContext } from '../order_context';
import type { OrderFormData } from '../order_schema';

interface PriceFieldProps {
  fieldName: keyof OrderFormData;
  showMidButton?: boolean;
}

export function PriceField({ fieldName, showMidButton = true }: PriceFieldProps) {
  const { t } = useLingui();
  const formContext = useFormContext<OrderFormData>();
  const { disabled, marketData, onPrefillMidPrice } = useOrderFormContext();
  const { handlePriceChange } = useOrderFormCoordinator({ priceFieldName: fieldName });

  const currentSide = useWatch({
    control: formContext.control,
    name: 'side',
  });

  const midPriceButtonClick = useCallback(() => {
    if (onPrefillMidPrice && currentSide) {
      onPrefillMidPrice(currentSide);
    }
  }, [onPrefillMidPrice, currentSide]);

  const fieldLabel = useMemo(() => t({ comment: 'Trade console form price label', message: 'Price' }), [t]);

  const priceToken = useMemo(
    () => ({
      symbol: marketData?.quote.symbol || 'USD',
      decimals: marketData?.quoteDecimals ?? 2,
      display_precision: marketData?.quoteDecimals ?? 2,
    }),
    [marketData?.quote?.symbol, marketData?.quoteDecimals]
  );

  const handleInputChange = useCallback(
    (value: string, onChange: (value: string) => void) => {
      onChange(value);
      handlePriceChange();
    },
    [handlePriceChange]
  );

  const endAdornment = useMemo(
    () => (
      <>
        <Typography variant="button" fontWeight={400} color="text.primary">
          USD
        </Typography>
        {showMidButton && (
          <InlineButton onClick={midPriceButtonClick} disabled={disabled} tabIndex={-1}>
            {t({
              comment:
                'Button that fills in the order book mid price. Keep the Latin `Mid` — it is the conventional label on trading terminals and the button is sized for three characters.',
              message: 'Mid',
            })}
          </InlineButton>
        )}
      </>
    ),
    [marketData?.quote?.symbol, showMidButton, midPriceButtonClick, disabled, t]
  );

  return (
    <Controller
      control={formContext.control}
      name={fieldName as any}
      render={({ field }) => (
        <DecimalInputField
          maxPrecision={priceToken.display_precision}
          value={field.value}
          label={fieldLabel}
          onChange={(value) => handleInputChange(value, field.onChange)}
          disabled={disabled}
          endAdornment={endAdornment}
          slotProps={{
            input: {
              sx: {
                pr: 0.5,
              },
            },
          }}
        />
      )}
    />
  );
}
